import { useCallback, useState, useRef, useEffect } from 'react';
import { MemeCanvas } from '../components/meme/MemeCanvas';
import { MemeNFTSearch } from '../components/meme/MemeNFTSearch';
import { Button, ImagePreviewModal, FontDropdown } from '../components/common';
import { useMemeStore, useUIStore, useThemeStore } from '../stores';
import { MEME_FONTS } from '../constants';
import { downloadImage, drawWrappedText, isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../utils';
import { playClickSound } from '../utils/gameSound';

// Icon components
function SearchIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function NubFaceIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-3-9c.83 0 1.5-.67 1.5-1.5S9.83 8 9 8s-1.5.67-1.5 1.5S8.17 11 9 11zm6 0c.83 0 1.5-.67 1.5-1.5S15.83 8 15 8s-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm-3 5.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
    </svg>
  );
}

function TextIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5 4v3h5.5v12h3V7H19V4H5z"/>
    </svg>
  );
}

function LayersIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function DownloadIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function TrashIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function BackgroundIcon({ className = '', active = false }: { className?: string; active?: boolean }) {
  return (
    <svg className={className} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 15l4-4 3 3 4-4 7 7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


// Toolbar Modal Component
type MenuType = 'editNft' | 'text' | 'background' | null;
const MOBILE_BOTTOM_SHEET_MAX_HEIGHT = 'min(56dvh, 520px)';
const MOBILE_BOTTOM_SHEET_HEADER_HEIGHT = 60;
const MOBILE_BOTTOM_SHEET_MIN_HEIGHT = 220;
const MOBILE_BOTTOM_SHEET_CLOSE_THRESHOLD = 130;

function getDefaultSheetHeight(): number {
  if (typeof window === 'undefined') return 420;
  return Math.min(window.innerHeight * 0.56, 520);
}

function getMaxSheetHeight(): number {
  if (typeof window === 'undefined') return 520;
  return Math.min(window.innerHeight - 80, 520);
}

function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  onHeightChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onHeightChange?: (height: number) => void;
}) {
  const [sheetHeight, setSheetHeight] = useState(getDefaultSheetHeight);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    const initialHeight = getDefaultSheetHeight();
    setSheetHeight(initialHeight);
    onHeightChange?.(initialHeight);
  }, [isOpen, onHeightChange]);

  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      const clamped = Math.max(
        MOBILE_BOTTOM_SHEET_MIN_HEIGHT,
        Math.min(sheetHeight, getMaxSheetHeight())
      );
      setSheetHeight(clamped);
      onHeightChange?.(clamped);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, sheetHeight, onHeightChange]);

  if (!isOpen) return null;

  const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    dragStartYRef.current = e.clientY;
    dragStartHeightRef.current = sheetHeight;
    setIsDraggingSheet(true);
  };

  const handleDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingSheet) return;
    const deltaY = dragStartYRef.current - e.clientY;
    const nextHeight = Math.min(
      getMaxSheetHeight(),
      Math.max(90, dragStartHeightRef.current + deltaY)
    );
    setSheetHeight(nextHeight);
    onHeightChange?.(nextHeight);
  };

  const handleDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingSheet) return;
    const target = e.currentTarget;
    target.releasePointerCapture(e.pointerId);
    setIsDraggingSheet(false);

    if (sheetHeight < MOBILE_BOTTOM_SHEET_CLOSE_THRESHOLD) {
      onClose();
      return;
    }

    const clamped = Math.max(
      MOBILE_BOTTOM_SHEET_MIN_HEIGHT,
      Math.min(sheetHeight, getMaxSheetHeight())
    );
    setSheetHeight(clamped);
    onHeightChange?.(clamped);
  };

  const contentHeight = Math.max(120, sheetHeight - MOBILE_BOTTOM_SHEET_HEADER_HEIGHT);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/35 z-40 pointer-events-none"
        aria-hidden="true"
      />
      {/* Sheet */}
      <div
        className={`fixed left-0 right-0 bottom-0 z-50 bg-neutral-900 rounded-t-3xl shadow-2xl mobile-bottom-sheet ${
          isDraggingSheet ? '' : 'animate-slide-up'
        }`}
        style={{
          height: sheetHeight,
          maxHeight: MOBILE_BOTTOM_SHEET_MAX_HEIGHT,
        }}
      >
        {/* Handle bar */}
        <div
          className="flex justify-center pt-2 pb-1 touch-none cursor-ns-resize"
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
        >
          <div className="w-10 h-1 bg-neutral-600 rounded-full" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-neutral-800">
          <h3 className="text-xs font-bold text-neutral-200 uppercase tracking-wide">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral-800 transition-colors text-neutral-400 hover:text-neutral-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content - scrollable */}
        <div
          className="overflow-y-auto overscroll-contain px-4 py-3"
          style={{
            maxHeight: contentHeight,
            paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}

function MenuDivider() {
  return <div className="border-t border-neutral-700 my-2" />;
}

export function StandaloneMemeBuilder() {
  const { theme } = useThemeStore();
  const { openModal } = useUIStore();
  const bgInputRef = useRef<HTMLInputElement>(null);
  const textPropertiesRef = useRef<HTMLDivElement>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [mobileTextDraft, setMobileTextDraft] = useState('');
  const [keyboardInset, setKeyboardInset] = useState(0);

  // Active menu state
  const [activeMenu, setActiveMenu] = useState<MenuType>(null);
  const [mobileSheetHeight, setMobileSheetHeight] = useState(0);

  // Preview modal state for Telegram fallback
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  useEffect(() => {
    if (!activeMenu) {
      setMobileSheetHeight(0);
    }
  }, [activeMenu]);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    document.documentElement.setAttribute('dir', 'ltr');
    document.body.setAttribute('dir', 'ltr');
  }, [theme]);

  const {
    canvasWidth,
    canvasHeight,
    backgroundImage,
    backgroundImageScale,
    backgroundImageOffset,
    backgroundColor,
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    selectElement,
    removeNFTElement,
    bringToFront,
    sendToBack,
    updateNFTElementLayers,
    setExclusiveNFTBackground,
    removeTextElement,
    updateTextElement,
    addTextElement,
    setBackgroundImage,
    setBackgroundColor,
  } = useMemeStore();

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;

      const img = new Image();
      img.onload = () => {
        setBackgroundImage(dataUrl, { width: img.width, height: img.height });
        nftElements.forEach((el) => {
          updateNFTElementLayers(el.id, el.selectedLayers, false, el.useFrameLayer);
        });
      };
      img.onerror = () => {
        setBackgroundImage(dataUrl);
        nftElements.forEach((el) => {
          updateNFTElementLayers(el.id, el.selectedLayers, false, el.useFrameLayer);
        });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  const handleClearBackground = () => {
    setBackgroundImage(null);
  };

  const handleAddNFT = () => {
    playClickSound();
    openModal('memeSearch');
  };

  const handleAddText = () => {
    playClickSound();
    addTextElement('');
  };

  const selectedNFT = selectedElementType === 'nft'
    ? nftElements.find((e) => e.id === selectedElementId)
    : null;

  const selectedText = selectedElementType === 'text'
    ? textElements.find((e) => e.id === selectedElementId)
    : null;

  // Auto-scroll to text properties when a text element is selected
  useEffect(() => {
    if (selectedText && textPropertiesRef.current) {
      setTimeout(() => {
        textPropertiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedText?.id]);

  useEffect(() => {
    const hasCharacterBackground = nftElements.some((element) => element.useBackgroundLayer);
    const targetColor = !backgroundImage && !hasCharacterBackground ? '#ffffff' : '#1a1a2e';
    if (backgroundColor !== targetColor) {
      setBackgroundColor(targetColor);
    }
  }, [backgroundColor, backgroundImage, nftElements, setBackgroundColor]);

  const enforceLtrInput = useCallback((input: HTMLInputElement) => {
    input.setAttribute('dir', 'ltr');
    input.style.setProperty('direction', 'ltr', 'important');
    input.style.setProperty('unicode-bidi', 'normal', 'important');
    input.style.setProperty('text-align', 'left', 'important');
    input.style.setProperty('writing-mode', 'horizontal-tb', 'important');
  }, []);

  const handleContentInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    if (!selectedText) return;
    const input = e.currentTarget;
    enforceLtrInput(input);
    updateTextElement(selectedText.id, { text: input.value });
  }, [selectedText, updateTextElement, enforceLtrInput]);

  const handleOpenMobileTextModal = useCallback(() => {
    if (!selectedText) return;
    setMobileTextDraft(selectedText.text);
    setIsTextModalOpen(true);
  }, [selectedText]);

  const handleSaveMobileText = useCallback(() => {
    if (!selectedText) return;
    updateTextElement(selectedText.id, { text: mobileTextDraft });
    setIsTextModalOpen(false);
  }, [selectedText, mobileTextDraft, updateTextElement]);

  useEffect(() => {
    const onResize = () => setIsNarrowViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const updateInset = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardInset(inset);
    };
    updateInset();
    viewport.addEventListener('resize', updateInset);
    viewport.addEventListener('scroll', updateInset);
    return () => {
      viewport.removeEventListener('resize', updateInset);
      viewport.removeEventListener('scroll', updateInset);
    };
  }, []);

  const hasBackgroundLayers = selectedNFT?.nftData.layerAssets.some(isBackgroundLayer);

  // Export handler
  const handleExport = useCallback(async () => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasWidth;
    exportCanvas.height = canvasHeight;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }

    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    if (backgroundImage) {
      try {
        const bgImg = await loadImage(backgroundImage);
        const scaledWidth = bgImg.width * backgroundImageScale;
        const scaledHeight = bgImg.height * backgroundImageScale;
        const drawX = (canvasWidth - scaledWidth) / 2 + backgroundImageOffset.x;
        const drawY = (canvasHeight - scaledHeight) / 2 + backgroundImageOffset.y;

        ctx.drawImage(bgImg, drawX, drawY, scaledWidth, scaledHeight);
      } catch (err) {
        console.error('Failed to load background image:', err);
      }
    }

    const sortedNFTElements = [...nftElements].sort((a, b) => a.zIndex - b.zIndex);

    // In sticker mode the canvas preview shows ONLY the sticker element (no text,
    // no other characters) — the export must match the preview.
    const stickerModeElement = nftElements.find((el) => el.stickerMode);

    const allElements: Array<{ type: 'nft' | 'text'; element: typeof nftElements[0] | typeof textElements[0] }> = stickerModeElement
      ? [{ type: 'nft' as const, element: stickerModeElement }]
      : [
          ...nftElements.map((e) => ({ type: 'nft' as const, element: e })),
          ...textElements.map((e) => ({ type: 'text' as const, element: e })),
        ].sort((a, b) => a.element.zIndex - b.element.zIndex);

    const drawBackgroundLayer = (layerImg: HTMLImageElement) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;
      let drawY: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        drawY = (canvasHeight - drawHeight) / 2;
      }

      ctx.drawImage(layerImg, drawX, drawY, drawWidth, drawHeight);
    };

    const shouldRenderNFTBackgrounds = !backgroundImage;

    // PASS 1: Draw ALL background layers first (in z-order)
    // Skipped entirely in sticker mode (mirrors MemeCanvas)
    if (shouldRenderNFTBackgrounds && !stickerModeElement) {
      for (const nftEl of sortedNFTElements) {
        if (!nftEl.useBackgroundLayer) continue;
        if (nftEl.stickerMode) continue;

        const backgroundLayers = nftEl.nftData.layerAssets.filter(isBackgroundLayer);

        for (const layer of backgroundLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            drawBackgroundLayer(layerImg);
          } catch (err) {
            console.error('Failed to load background layer for export:', layer.id, err);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers and text elements (in z-order)
    for (const { type, element } of allElements) {
      ctx.save();

      if (type === 'nft') {
        const nftEl = element as typeof nftElements[0];
        const layers = nftEl.nftData.layerAssets;

        ctx.translate(nftEl.position.x, nftEl.position.y);
        ctx.rotate((nftEl.rotation * Math.PI) / 180);

        if (layers.length > 0) {
          const characterLayers = layers.filter((layer) => {
            if (nftEl.selectedLayers.length > 0) {
              return nftEl.selectedLayers.includes(layer.id);
            }

            // Background/frame handled in pass 1 / pass 3
            return isCharacterLayer(layer);
          });

          let refWidth = 512;
          let refHeight = 512;
          try {
            const refImg = await loadImage(nftEl.nftData.imageUrl);
            refWidth = refImg.width;
            refHeight = refImg.height;
          } catch {
            // Use default dimensions if composite image fails to load
          }

          for (const layer of characterLayers) {
            try {
              const layerImg = await loadImage(layer.uri);
              const scaledWidth = refWidth * nftEl.scale;
              const scaledHeight = refHeight * nftEl.scale;
              ctx.drawImage(
                layerImg,
                -scaledWidth / 2,
                -scaledHeight / 2,
                scaledWidth,
                scaledHeight
              );
            } catch (err) {
              console.error('Failed to load layer for export:', layer.id, err);
            }
          }
        } else {
          try {
            const img = await loadImage(nftEl.nftData.imageUrl);
            const scaledWidth = img.width * nftEl.scale;
            const scaledHeight = img.height * nftEl.scale;
            ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          } catch (err) {
            console.error('Failed to load NFT image for export:', err);
          }
        }
      } else {
        const textEl = element as typeof textElements[0];
        ctx.translate(textEl.position.x, textEl.position.y);
        ctx.rotate((textEl.rotation * Math.PI) / 180);

        ctx.direction = 'ltr';
        ctx.font = `${textEl.fontSize}px ${textEl.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        drawWrappedText(ctx, textEl.text, textEl.fontSize, canvasWidth * 0.9, {
          color: textEl.color,
          strokeColor: textEl.strokeColor,
          strokeWidth: textEl.strokeWidth,
        });
      }

      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last (in z-order)
    // Skipped entirely in sticker mode (mirrors MemeCanvas)
    if (!stickerModeElement)
    for (const nftEl of sortedNFTElements) {
      if (!nftEl.useFrameLayer) continue;
      if (nftEl.stickerMode) continue;

      const frameLayers = nftEl.nftData.layerAssets.filter(isFrameLayer);

      for (const layer of frameLayers) {
        try {
          const layerImg = await loadImage(layer.uri);
          drawBackgroundLayer(layerImg);
        } catch (err) {
          console.error('Failed to load frame layer for export:', layer.id, err);
        }
      }
    }

    exportCanvas.toBlob(async (blob) => {
      if (blob) {
        const filename = `nft_meme_${canvasWidth}x${canvasHeight}.png`;

        await downloadImage(blob, filename, {
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
        });
      }
    }, 'image/png');
  }, [canvasWidth, canvasHeight, backgroundImage, backgroundImageScale, backgroundImageOffset, backgroundColor, nftElements, textElements]);

  return (
    <div
      dir="ltr"
      className="bg-neutral-950 flex flex-col overflow-hidden"
      style={{ height: '100dvh' }}
    >
      {/* Search Bar at Top */}
      <div className="flex items-center justify-center px-4 py-3">
        <button
          onClick={handleAddNFT}
          className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-game-blue to-game-purple text-white font-semibold shadow-lg hover:opacity-90 transition-all active:scale-95"
          aria-label="Search NFT"
        >
          <SearchIcon className="w-5 h-5" />
          <span>Search NFT</span>
        </button>
      </div>

      {/* Canvas Area */}
      <div
        className="flex-1 flex items-center justify-center px-4 py-2 transition-all duration-300"
        style={{
          paddingBottom: activeMenu
            ? `calc(${Math.max(mobileSheetHeight, MOBILE_BOTTOM_SHEET_MIN_HEIGHT)}px + env(safe-area-inset-bottom, 0px))`
            : undefined,
        }}
      >
        <div className="w-full max-w-md">
          <MemeCanvas
            maxViewportWidth={400}
            maxViewportHeight={400}
            onCharacterTap={() => setActiveMenu('editNft')}
          />
        </div>
      </div>

      {/* Bottom Toolbar */}
      <div className="flex items-center justify-center gap-3 px-4 py-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
        {/* Text Menu Button */}
        <button
          onClick={() => {
            playClickSound();
            setActiveMenu(activeMenu === 'text' ? null : 'text');
          }}
          className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all active:scale-95 ${
            activeMenu === 'text'
              ? 'bg-game-blue text-white'
              : 'bg-white text-neutral-800 hover:bg-neutral-100'
          }`}
          aria-label="Text"
          title="Text"
        >
          <TextIcon className="w-7 h-7" />
        </button>

        {/* Edit NFT Button */}
        <button
          onClick={() => {
            playClickSound();
            setActiveMenu(activeMenu === 'editNft' ? null : 'editNft');
          }}
          className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all active:scale-95 ${
            activeMenu === 'editNft'
              ? 'bg-game-blue text-white'
              : 'bg-white text-neutral-800 hover:bg-neutral-100'
          }`}
          aria-label="Edit NFT"
          title="Edit NFT"
        >
          <NubFaceIcon className="w-7 h-7" />
        </button>

        {/* Add Background Button */}
        <button
          onClick={() => {
            playClickSound();
            setActiveMenu(activeMenu === 'background' ? null : 'background');
          }}
          className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all active:scale-95 ${
            activeMenu === 'background'
              ? 'bg-game-blue text-white'
              : 'bg-white text-neutral-800 hover:bg-neutral-100'
          }`}
          aria-label="Add Background"
          title="Add Background"
        >
          <LayersIcon className="w-7 h-7" />
        </button>

        {/* Save Button */}
        <button
          onClick={handleExport}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-white text-neutral-800 shadow-lg hover:bg-neutral-100 transition-all active:scale-95"
          aria-label="Save"
          title="Save PNG"
        >
          <DownloadIcon className="w-7 h-7" />
        </button>
      </div>

      {/* Edit NFT Modal */}
      <BottomSheet
        isOpen={activeMenu === 'editNft'}
        onClose={() => setActiveMenu(null)}
        title="Edit NFT"
        onHeightChange={setMobileSheetHeight}
      >
        <div className="space-y-4">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              handleAddNFT();
              setActiveMenu(null);
            }}
            className="w-full"
          >
            + Add NFT
          </Button>

          <MenuDivider />
          <div className="space-y-2">
            {nftElements.length === 0 && (
              <p className="text-xs text-neutral-400">No subject selected yet. Tap "Add NFT" to add one.</p>
            )}

            {nftElements.map((nft) => (
              <div
                key={nft.id}
                onClick={() => selectElement(nft.id, 'nft')}
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${
                  selectedElementId === nft.id
                    ? 'bg-game-blue/10 border-game-blue'
                    : 'bg-neutral-700/30 border-transparent hover:border-neutral-600'
                }`}
              >
                <img
                  src={nft.nftData.imageUrl}
                  alt={nft.nftData.name}
                  className="w-10 h-10 rounded object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-neutral-200 truncate">
                    {nft.nftData.name}
                  </p>
                </div>
                {nft.nftData.layerAssets.some(isBackgroundLayer) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExclusiveNFTBackground(nft.id, !nft.useBackgroundLayer);
                    }}
                    className={`p-1.5 rounded-md transition-colors ${
                      nft.useBackgroundLayer
                        ? 'bg-game-blue/20 text-game-blue hover:bg-game-blue/30'
                        : 'hover:bg-neutral-700 text-neutral-500 hover:text-neutral-400'
                    }`}
                    title={nft.useBackgroundLayer ? 'Disable background' : 'Enable background'}
                    aria-label={`${nft.useBackgroundLayer ? 'Disable' : 'Enable'} background for ${nft.nftData.name}`}
                  >
                    <BackgroundIcon className="w-4 h-4" active={nft.useBackgroundLayer} />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNFTElement(nft.id);
                  }}
                  className="p-1.5 rounded-md hover:bg-red-400/10 text-neutral-500 hover:text-red-400 transition-colors"
                  aria-label={`Delete ${nft.nftData.name}`}
                  title="Delete NFT"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {selectedNFT && hasBackgroundLayers && <MenuDivider />}
        </div>
      </BottomSheet>

      {/* Text Modal */}
      <BottomSheet
        isOpen={activeMenu === 'text'}
        onClose={() => setActiveMenu(null)}
        title="Text"
        onHeightChange={setMobileSheetHeight}
      >
        <div className="space-y-4">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              handleAddText();
            }}
            className="w-full"
          >
            <span className="mr-2">+</span>
            Add New Text
          </Button>

          {textElements.length > 0 && (
            <>
              <MenuDivider />
              <div className="space-y-2">
                {textElements.map((text) => (
                  <div
                    key={text.id}
                    onClick={() => selectElement(text.id, 'text')}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${
                      selectedElementId === text.id
                        ? 'bg-game-blue/10 border-game-blue'
                        : 'bg-neutral-700/30 border-transparent hover:border-neutral-600'
                    }`}
                  >
                    <div className="w-10 h-10 rounded bg-neutral-600 flex items-center justify-center">
                      <span className="font-bold text-neutral-300">T</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-neutral-200 truncate">
                        {text.text}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTextElement(text.id);
                      }}
                      className="p-1.5 rounded-md hover:bg-red-400/10 text-neutral-500 hover:text-red-400 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Selected Text Properties */}
          {selectedText && (
            <>
              <MenuDivider />
              <div ref={textPropertiesRef} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Content</label>
                  {isNarrowViewport ? (
                    <button
                      type="button"
                      onClick={handleOpenMobileTextModal}
                      className="w-full px-3 py-2 text-left bg-neutral-700 border border-neutral-600 rounded-lg text-sm text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                    >
                      {selectedText.text || 'Tap to edit text'}
                    </button>
                  ) : (
                    <input
                      key={selectedText.id}
                      type="text"
                      dir="ltr"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      defaultValue={selectedText.text}
                      placeholder="YOUR TEXT HERE"
                      onFocus={(e) => enforceLtrInput(e.currentTarget)}
                      onInput={handleContentInput}
                      className="meme-text-input w-full px-3 py-2 text-left bg-neutral-700 border border-neutral-600 rounded-lg text-sm text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Font</label>
                  <FontDropdown
                    value={selectedText.fontFamily}
                    options={MEME_FONTS}
                    onChange={(font) => updateTextElement(selectedText.id, { fontFamily: font })}
                    buttonClassName="bg-neutral-700 border border-neutral-600 text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                    menuClassName="bg-neutral-800 border-neutral-600 text-neutral-200"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Size: {selectedText.fontSize}px
                  </label>
                  <input
                    type="range"
                    min="12"
                    max="200"
                    step="2"
                    value={selectedText.fontSize}
                    onChange={(e) => updateTextElement(selectedText.id, { fontSize: parseInt(e.target.value) })}
                    className="w-full h-1.5 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Fill</label>
                    <input
                      type="color"
                      value={selectedText.color}
                      onChange={(e) => updateTextElement(selectedText.id, { color: e.target.value })}
                      className="w-full h-8 rounded border border-neutral-600 cursor-pointer overflow-hidden"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Stroke</label>
                    <input
                      type="color"
                      value={selectedText.strokeColor}
                      onChange={(e) => updateTextElement(selectedText.id, { strokeColor: e.target.value })}
                      className="w-full h-8 rounded border border-neutral-600 cursor-pointer overflow-hidden"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Stroke: {selectedText.strokeWidth}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={selectedText.strokeWidth}
                    onChange={(e) => updateTextElement(selectedText.id, { strokeWidth: parseInt(e.target.value) })}
                    className="w-full h-1.5 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => bringToFront(selectedText.id, 'text')}
                    className="flex-1 text-[10px]"
                  >
                    Bring Front
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => sendToBack(selectedText.id, 'text')}
                    className="flex-1 text-[10px]"
                  >
                    Send Back
                  </Button>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTextElement(selectedText.id)}
                  className="w-full text-[10px] text-red-400 hover:bg-red-400/10"
                >
                  Delete Text
                </Button>
              </div>
            </>
          )}
        </div>
      </BottomSheet>

      {/* Background Modal */}
      <BottomSheet
        isOpen={activeMenu === 'background'}
        onClose={() => setActiveMenu(null)}
        title="Add Background"
        onHeightChange={setMobileSheetHeight}
      >
        <div className="space-y-4">
          <Button
            variant="primary"
            size="sm"
            onClick={() => bgInputRef.current?.click()}
            className="w-full"
          >
            + Add Background
          </Button>

          {backgroundImage && (
            <>
              <div className="relative rounded-lg overflow-hidden border border-neutral-600">
                <img
                  src={backgroundImage}
                  alt="Background preview"
                  className="w-full h-24 object-cover"
                />
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearBackground}
                className="w-full text-xs text-red-400 hover:bg-red-400/10"
              >
                Remove Background
              </Button>
            </>
          )}

        </div>
      </BottomSheet>

      {/* Hidden file input for background upload */}
      <input
        ref={bgInputRef}
        type="file"
        accept="image/*"
        onChange={handleBackgroundUpload}
        className="hidden"
      />

      {/* NFT Search Modal */}
      <MemeNFTSearch />

      {isTextModalOpen && selectedText && (
        <div className="fixed inset-0 z-[120]">
          <button
            type="button"
            aria-label="Close text editor"
            className="absolute inset-0 bg-black/45"
            onClick={() => setIsTextModalOpen(false)}
          />
          <div
            className="absolute left-3 right-3 rounded-2xl border border-neutral-700 bg-neutral-900 shadow-2xl p-4"
            style={{ bottom: `${Math.max(12, keyboardInset + 12)}px` }}
          >
            <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
              Edit Text
            </label>
            <input
              type="text"
              dir="ltr"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={mobileTextDraft}
              onFocus={(e) => enforceLtrInput(e.currentTarget)}
              onChange={(e) => setMobileTextDraft(e.target.value)}
              className="meme-text-input w-full px-3 py-2 text-left bg-neutral-800 border border-neutral-700 rounded-lg text-sm text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
            />
            <div className="mt-3 flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsTextModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveMobileText}
                className="flex-1"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal for Telegram fallback */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url || ''}
        filename={previewImage?.filename || ''}
      />
    </div>
  );
}

export default StandaloneMemeBuilder;
